import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { 
  Church, MapPin, MessageCircle, Edit2, ShoppingBag, FileText, Tag, Star, 
  Activity, Eye, EyeOff, Heart, Package, Calendar, Users, HandHeart, 
  ShoppingCart, Truck, Check, UserPlus, UserCheck, UserX, Clock, Loader2, 
  Languages, Home, Globe, Mail, Link as LinkIcon, Briefcase, GraduationCap, 
  Plus, Pencil, Trash2, Building2, Cross, BookOpen, Crown, Shield, Award,
  Scale, Book, Quote, Sparkles, Facebook, Instagram, Twitter, Linkedin, Youtube,
  ExternalLink, AlertCircle, Camera, ImagePlus, X, Settings, Lock, Image, Bell, BellOff,
  Share2, Repeat2, Bookmark, Send, Store
} from "lucide-react";
import { 
  getUserProfile, updateProfile, sendMessage, getUserActivity, updateActivityPrivacy, 
  getActivityPrivacy, getUserReviews, resolveImageUrl, getFriendshipStatus, 
  sendFriendRequest, acceptFriendRequest, cancelFriendRequest, unfriend, 
  getAvailableLanguages, getUserFriends, getUserFamily, getFamilyStatus,
  addWorkEntry, updateWorkEntry, deleteWorkEntry, addEducationEntry, 
  updateEducationEntry, deleteEducationEntry, getReligiousRoles, getRelationshipStatuses,
  getProfileCompleteness, uploadImage, changePassword, followUser, unfollowUser, getFollowStatus,
  likePost, toggleBookmark, getListings, getUserRequests,
  togglePrayRequest, deleteRequest, updateRequest, searchUsers
} from "@/lib/api";
import { toast } from "sonner";
import api from "@/lib/api";
import { PostCard } from "@/pages/DashboardPage";
import { ListingCard } from "@/pages/RequestsPage";
import MediaGallery from "@/components/profile/MediaGallery";
import ImageCropper from "@/components/ImageCropper";

const ACTIVITY_ICONS = {
  post: FileText,
  listing: ShoppingBag,
  purchase: ShoppingCart,
  sale: Package,
  rsvp: Calendar,
  prayer: HandHeart,
  like: Heart,
  comment: MessageCircle,
  review: Star
};

const ACTIVITY_COLORS = {
  post: "bg-blue-100 text-blue-700",
  listing: "bg-purple-100 text-purple-700",
  purchase: "bg-green-100 text-green-700",
  sale: "bg-amber-100 text-amber-700",
  rsvp: "bg-pink-100 text-pink-700",
  prayer: "bg-indigo-100 text-indigo-700",
  like: "bg-red-100 text-red-700",
  comment: "bg-cyan-100 text-cyan-700",
  review: "bg-yellow-100 text-yellow-700"
};

function StarRating({ rating, size = "sm" }) {
  const stars = [];
  const starSize = size === "sm" ? "w-3.5 h-3.5" : "w-5 h-5";
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`${starSize} ${i <= rating ? "text-[var(--liturgical-secondary)] fill-[#D4AF37]" : "text-[#E5E0D5]"}`}
      />
    );
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

function ActivityFeed({ activities, isOwnProfile }) {
  if (!activities || activities.length === 0) {
    return (
      <div className="text-center py-12">
        <Activity className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
        <p className="text-sm text-[#6B665E] font-['Nunito']">
          {isOwnProfile ? "No activity yet" : "Activity is private"}
        </p>
      </div>
    );
  }

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-3">
      {activities.map((activity) => {
        const Icon = ACTIVITY_ICONS[activity.activity_type] || Activity;
        const colorClass = ACTIVITY_COLORS[activity.activity_type] || "bg-gray-100 text-gray-700";
        
        return (
          <div
            key={activity.activity_id}
            className="flex items-start gap-3 p-3 bg-[#F9F7F2] rounded-lg"
            data-testid={`activity-${activity.activity_id}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#2D2A26] font-['Nunito']">{activity.description}</p>
              <p className="text-xs text-[#6B665E] font-['Nunito'] mt-0.5">{timeAgo(activity.created_at)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReviewsList({ reviews }) {
  if (!reviews || reviews.length === 0) {
    return (
      <div className="text-center py-12">
        <Star className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
        <p className="text-sm text-[#6B665E] font-['Nunito']">No reviews yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <Card key={review.review_id} className="bg-white border-[#E5E0D5]" data-testid={`review-${review.review_id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={resolveImageUrl(review.reviewer?.picture)} />
                  <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">
                    {review.reviewer?.name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-['Nunito'] font-semibold text-[#2D2A26]">{review.reviewer?.name}</p>
                  <StarRating rating={review.rating} />
                </div>
              </div>
              <span className="text-xs text-[#6B665E] font-['Nunito']">
                {new Date(review.created_at).toLocaleDateString()}
              </span>
            </div>
            {review.content && (
              <p className="text-sm text-[#2D2A26] font-['Nunito'] mt-3 leading-relaxed">{review.content}</p>
            )}
            {review.listing && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E5E0D5]">
                {review.listing.image_url && (
                  <img 
                    src={resolveImageUrl(review.listing.image_url)} 
                    alt={review.listing.title} 
                    className="w-10 h-10 rounded object-cover"
                  />
                )}
                <span className="text-xs text-[#6B665E] font-['Nunito']">
                  Purchased: {review.listing.title}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Current year for validation
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 60 }, (_, i) => CURRENT_YEAR - i);

// Work Entry Modal Component
function WorkEntryModal({ isOpen, onClose, onSave, entry, isLoading }) {
  const [employer, setEmployer] = useState(entry?.employer || "");
  const [position, setPosition] = useState(entry?.position || "");
  const [startYear, setStartYear] = useState(entry?.start_year || "");
  const [endYear, setEndYear] = useState(entry?.end_year || "");
  const [isCurrent, setIsCurrent] = useState(entry?.current || false);

  useEffect(() => {
    if (entry) {
      setEmployer(entry.employer || "");
      setPosition(entry.position || "");
      setStartYear(entry.start_year || "");
      setEndYear(entry.end_year || "");
      setIsCurrent(entry.current || false);
    } else {
      setEmployer("");
      setPosition("");
      setStartYear("");
      setEndYear("");
      setIsCurrent(false);
    }
  }, [entry, isOpen]);

  const handleSubmit = () => {
    if (!employer.trim() || !position.trim() || !startYear) {
      toast.error("Please fill in required fields");
      return;
    }
    onSave({
      employer: employer.trim(),
      position: position.trim(),
      start_year: parseInt(startYear),
      end_year: isCurrent ? null : (endYear ? parseInt(endYear) : null),
      current: isCurrent
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[var(--liturgical-primary)]" />
            {entry ? "Edit Work Experience" : "Add Work Experience"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Employer *</label>
            <Input 
              value={employer} 
              onChange={e => setEmployer(e.target.value)} 
              placeholder="Company or organization name"
              className="border-[#E5E0D5] font-['Nunito'] text-sm" 
              data-testid="work-employer-input"
            />
          </div>
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Position *</label>
            <Input 
              value={position} 
              onChange={e => setPosition(e.target.value)} 
              placeholder="Your job title"
              className="border-[#E5E0D5] font-['Nunito'] text-sm" 
              data-testid="work-position-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Start Year *</label>
              <Select value={startYear?.toString()} onValueChange={setStartYear}>
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="work-start-year">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-48">
                  {YEAR_OPTIONS.map(year => (
                    <SelectItem key={year} value={year.toString()} className="font-['Nunito']">{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">End Year</label>
              <Select 
                value={endYear?.toString() || ""} 
                onValueChange={setEndYear}
                disabled={isCurrent}
              >
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="work-end-year">
                  <SelectValue placeholder={isCurrent ? "Present" : "Year"} />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-48">
                  {YEAR_OPTIONS.map(year => (
                    <SelectItem key={year} value={year.toString()} className="font-['Nunito']">{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#F9F7F2] rounded-lg">
            <Switch 
              checked={isCurrent} 
              onCheckedChange={(checked) => {
                setIsCurrent(checked);
                if (checked) setEndYear("");
              }}
              data-testid="work-current-toggle"
            />
            <Label className="font-['Nunito'] text-sm text-[#2D2A26]">I currently work here</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="flex-1 border-[#E5E0D5] font-['Nunito']"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isLoading}
              className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
              data-testid="save-work-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Education Entry Modal Component
function EducationEntryModal({ isOpen, onClose, onSave, entry, isLoading }) {
  const [institution, setInstitution] = useState(entry?.institution || "");
  const [degree, setDegree] = useState(entry?.degree || "");
  const [fieldOfStudy, setFieldOfStudy] = useState(entry?.field_of_study || "");
  const [startYear, setStartYear] = useState(entry?.start_year || "");
  const [endYear, setEndYear] = useState(entry?.end_year || "");
  const [isCurrent, setIsCurrent] = useState(entry?.current || false);

  useEffect(() => {
    if (entry) {
      setInstitution(entry.institution || "");
      setDegree(entry.degree || "");
      setFieldOfStudy(entry.field_of_study || "");
      setStartYear(entry.start_year || "");
      setEndYear(entry.end_year || "");
      setIsCurrent(entry.current || false);
    } else {
      setInstitution("");
      setDegree("");
      setFieldOfStudy("");
      setStartYear("");
      setEndYear("");
      setIsCurrent(false);
    }
  }, [entry, isOpen]);

  const handleSubmit = () => {
    if (!institution.trim() || !degree.trim() || !startYear) {
      toast.error("Please fill in required fields");
      return;
    }
    onSave({
      institution: institution.trim(),
      degree: degree.trim(),
      field_of_study: fieldOfStudy.trim() || null,
      start_year: parseInt(startYear),
      end_year: isCurrent ? null : (endYear ? parseInt(endYear) : null),
      current: isCurrent
    });
  };

  const DEGREE_OPTIONS = [
    "High School Diploma",
    "Associate Degree",
    "Bachelor's Degree",
    "Master's Degree",
    "Doctorate (PhD)",
    "Professional Degree (MD, JD, etc.)",
    "Certificate",
    "Other"
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[var(--liturgical-primary)]" />
            {entry ? "Edit Education" : "Add Education"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Institution *</label>
            <Input 
              value={institution} 
              onChange={e => setInstitution(e.target.value)} 
              placeholder="School or university name"
              className="border-[#E5E0D5] font-['Nunito'] text-sm" 
              data-testid="edu-institution-input"
            />
          </div>
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Degree *</label>
            <Select value={degree} onValueChange={setDegree}>
              <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edu-degree-select">
                <SelectValue placeholder="Select degree type" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {DEGREE_OPTIONS.map(deg => (
                  <SelectItem key={deg} value={deg} className="font-['Nunito']">{deg}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Field of Study</label>
            <Input 
              value={fieldOfStudy} 
              onChange={e => setFieldOfStudy(e.target.value)} 
              placeholder="e.g., Computer Science, Theology"
              className="border-[#E5E0D5] font-['Nunito'] text-sm" 
              data-testid="edu-field-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Start Year *</label>
              <Select value={startYear?.toString()} onValueChange={setStartYear}>
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edu-start-year">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-48">
                  {YEAR_OPTIONS.map(year => (
                    <SelectItem key={year} value={year.toString()} className="font-['Nunito']">{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">End Year</label>
              <Select 
                value={endYear?.toString() || ""} 
                onValueChange={setEndYear}
                disabled={isCurrent}
              >
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edu-end-year">
                  <SelectValue placeholder={isCurrent ? "Present" : "Year"} />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-48">
                  {YEAR_OPTIONS.map(year => (
                    <SelectItem key={year} value={year.toString()} className="font-['Nunito']">{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-[#F9F7F2] rounded-lg">
            <Switch 
              checked={isCurrent} 
              onCheckedChange={(checked) => {
                setIsCurrent(checked);
                if (checked) setEndYear("");
              }}
              data-testid="edu-current-toggle"
            />
            <Label className="font-['Nunito'] text-sm text-[#2D2A26]">I'm currently studying here</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="flex-1 border-[#E5E0D5] font-['Nunito']"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isLoading}
              className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
              data-testid="save-edu-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProfilePage({ currentUser }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editLanguage, setEditLanguage] = useState("");
  const [editReligiousRole, setEditReligiousRole] = useState("");
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [availableReligiousRoles, setAvailableReligiousRoles] = useState([]);
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [activities, setActivities] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activityPublic, setActivityPublic] = useState(false);
  const [activeTab, setActiveTab] = useState("about");
  
  // Friend/Family state
  const [friendStatus, setFriendStatus] = useState({ status: "none" });
  const [familyStatus, setFamilyStatus] = useState({ status: "none" });
  const [friendLoading, setFriendLoading] = useState(false);
  const [userFriends, setUserFriends] = useState([]);
  const [userFamily, setUserFamily] = useState([]);
  const [mutualFriends, setMutualFriends] = useState([]);
  
  // Work & Education state
  const [workHistory, setWorkHistory] = useState([]);
  const [educationHistory, setEducationHistory] = useState([]);
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [educationModalOpen, setEducationModalOpen] = useState(false);
  const [editingWork, setEditingWork] = useState(null);
  const [editingEducation, setEditingEducation] = useState(null);
  const [savingWork, setSavingWork] = useState(false);
  const [savingEducation, setSavingEducation] = useState(false);
  
  // New profile fields state
  const [editRelationshipStatus, setEditRelationshipStatus] = useState("");
  const [editConfirmationSaint, setEditConfirmationSaint] = useState("");
  const [editFavoriteQuote, setEditFavoriteQuote] = useState("");
  const [editSocialLinks, setEditSocialLinks] = useState({});
  const [editSavedZipCode, setEditSavedZipCode] = useState("");
  const [editSavedCountry, setEditSavedCountry] = useState("us");
  const [editGender, setEditGender] = useState("");
  const [editBirthDate, setEditBirthDate] = useState("");
  const [availableRelationshipStatuses, setAvailableRelationshipStatuses] = useState([]);
  const [profileCompleteness, setProfileCompleteness] = useState(null);
  const [editPicture, setEditPicture] = useState("");
  const [editCoverImage, setEditCoverImage] = useState("");
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  
  // Image cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  
  // Password change state
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Follow state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  
  // Offers & Requests state
  const [userRequests, setUserRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [reqShareTarget, setReqShareTarget] = useState(null);
  const [showReqShareDialog, setShowReqShareDialog] = useState(false);
  const [reqShareSearchQuery, setReqShareSearchQuery] = useState("");
  const [reqShareResults, setReqShareResults] = useState([]);
  const [reqSharing, setReqSharing] = useState(false);
  const [reqEditListing, setReqEditListing] = useState(null);
  const [reqEditTitle, setReqEditTitle] = useState("");
  const [reqEditContent, setReqEditContent] = useState("");
  const [reqEditDialogOpen, setReqEditDialogOpen] = useState(false);
  const [reqEditSaving, setReqEditSaving] = useState(false);

  const isOwnProfile = !userId || userId === currentUser.user_id;
  const profileId = isOwnProfile ? currentUser.user_id : userId;

  useEffect(() => {
    loadProfile();
    loadLanguages();
    loadReligiousRoles();
    loadRelationshipStatuses();
    if (isOwnProfile) {
      loadProfileCompleteness();
    }
  }, [profileId]);

  const loadLanguages = async () => {
    try {
      const res = await getAvailableLanguages();
      setAvailableLanguages(res.data.languages || []);
    } catch {
      setAvailableLanguages(["English", "Spanish", "Portuguese", "French", "Italian", "Polish", "German", "Filipino/Tagalog", "Vietnamese", "Korean", "Latin", "Other"]);
    }
  };

  const loadReligiousRoles = async () => {
    try {
      const res = await getReligiousRoles();
      setAvailableReligiousRoles(res.data.roles || []);
    } catch {
      setAvailableReligiousRoles([]);
    }
  };
  
  const loadRelationshipStatuses = async () => {
    try {
      const res = await getRelationshipStatuses();
      setAvailableRelationshipStatuses(res.data.statuses || []);
    } catch {
      setAvailableRelationshipStatuses([]);
    }
  };
  
  const loadProfileCompleteness = async () => {
    try {
      const res = await getProfileCompleteness();
      setProfileCompleteness(res.data);
    } catch {
      setProfileCompleteness(null);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const [profileRes, activityRes, reviewsRes, friendsRes, familyRes] = await Promise.all([
        getUserProfile(profileId),
        getUserActivity(profileId).catch(() => ({ data: [] })),
        getUserReviews(profileId).catch(() => ({ data: [] })),
        getUserFriends(profileId).catch(() => ({ data: { friends: [] } })),
        getUserFamily(profileId).catch(() => ({ data: { family: [] } }))
      ]);
      
      setProfile(profileRes.data);
      
      // Extract user data from profile response
      const userData = profileRes.data.user || profileRes.data;
      
      setEditName(userData.name || "");
      setEditBio(userData.bio || "");
      setEditLanguage(userData.language || "");
      setEditReligiousRole(userData.religious_role || "");
      setActivities(activityRes.data);
      setReviews(reviewsRes.data);
      setActivityPublic(profileRes.data.activity_public || userData.activity_public || false);
      setUserFriends(friendsRes.data.friends || []);
      setUserFamily(familyRes.data.family || []);
      
      // Work and education history
      setWorkHistory(userData.work_history || []);
      setEducationHistory(userData.education_history || []);
      
      // Set new profile fields
      setEditRelationshipStatus(userData.relationship_status || "");
      setEditConfirmationSaint(userData.confirmation_saint || "");
      setEditFavoriteQuote(userData.favorite_quote || "");
      setEditSocialLinks(userData.social_links || {});
      setEditPicture(userData.picture || "");
      setEditCoverImage(userData.cover_image || "");
      setEditSavedZipCode(userData.saved_zip_code || "");
      setEditSavedCountry(userData.saved_country || "us");
      setEditGender(userData.gender || "");
      setEditBirthDate(userData.birth_date || "");
      
      // Load friendship/family status for other users
      if (!isOwnProfile) {
        try {
          const [friendRes, famRes, followRes] = await Promise.all([
            getFriendshipStatus(profileId),
            getFamilyStatus(profileId).catch(() => ({ data: { status: "none" } })),
            getFollowStatus(profileId).catch(() => ({ data: { is_following: false, followers_count: 0, following_count: 0 } }))
          ]);
          setFriendStatus(friendRes.data);
          setFamilyStatus(famRes.data);
          setIsFollowing(followRes.data.is_following);
          setFollowersCount(followRes.data.followers_count);
          setFollowingCount(followRes.data.following_count);
          
          // Calculate mutual friends
          const myFriendsRes = await getUserFriends(currentUser.user_id).catch(() => ({ data: { friends: [] } }));
          const myFriendIds = new Set(myFriendsRes.data.friends?.map(f => f.user_id) || []);
          const mutual = friendsRes.data.friends?.filter(f => myFriendIds.has(f.user_id)) || [];
          setMutualFriends(mutual);
        } catch {
          setFriendStatus({ status: "none" });
          setFamilyStatus({ status: "none" });
        }
      } else {
        // Load own follower/following counts
        try {
          const followRes = await getFollowStatus(profileId);
          setFollowersCount(followRes.data.followers_count);
          setFollowingCount(followRes.data.following_count);
        } catch {}
      }
    } catch {
      toast.error("Failed to load profile");
    }
    setLoading(false);
    
    // Load user requests/offers in background
    try {
      const reqRes = await getUserRequests(profileId);
      setUserRequests(reqRes.data.requests || []);
    } catch { setUserRequests([]); }
  };

  const handleFriendAction = async () => {
    setFriendLoading(true);
    try {
      if (friendStatus.status === "none") {
        // Send friend request
        const res = await sendFriendRequest(profileId);
        setFriendStatus({ status: "pending_sent", friendship_id: res.data.friendship_id });
        toast.success("Friend request sent!");
      } else if (friendStatus.status === "pending_received") {
        // Accept friend request
        await acceptFriendRequest(friendStatus.friendship_id);
        setFriendStatus({ status: "friends" });
        toast.success("Friend request accepted!");
      } else if (friendStatus.status === "pending_sent") {
        // Cancel sent request
        await cancelFriendRequest(friendStatus.friendship_id);
        setFriendStatus({ status: "none" });
        toast.success("Friend request cancelled");
      } else if (friendStatus.status === "friends") {
        // Unfriend
        if (window.confirm(`Remove ${profile?.name} from your friends?`)) {
          await unfriend(profileId);
          setFriendStatus({ status: "none" });
          toast.success("Removed from friends");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update friendship");
    }
    setFriendLoading(false);
  };

  const getFriendButtonConfig = () => {
    switch (friendStatus.status) {
      case "friends":
        return { icon: UserCheck, label: "Friends", variant: "outline", className: "border-[var(--liturgical-primary)] text-[var(--liturgical-primary)]" };
      case "pending_sent":
        return { icon: Clock, label: "Pending", variant: "outline", className: "border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)]" };
      case "pending_received":
        return { icon: UserPlus, label: "Accept", variant: "default", className: "bg-[var(--liturgical-primary)] hover:brightness-90 text-white" };
      default:
        return { icon: UserPlus, label: "Add Friend", variant: "outline", className: "border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5" };
    }
  };

  const handleFollowAction = async () => {
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await unfollowUser(profileId);
        setIsFollowing(false);
        setFollowersCount(prev => Math.max(0, prev - 1));
        toast.success(`Unfollowed ${profile?.name}`);
      } else {
        await followUser(profileId);
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
        toast.success(`Now following ${profile?.name}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update follow status");
    }
    setFollowLoading(false);
  };

  const handlePictureUpload = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image must be less than 10MB");
        return;
      }
      
      // Read file and open cropper
      const reader = new FileReader();
      reader.onload = () => {
        setCropperImage(reader.result);
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
      
      // Reset input so same file can be selected again
      e.target.value = '';
    } catch (err) {
      console.error("Error reading file:", err);
      toast.error("Failed to read image file");
    }
  };

  const handleCroppedImage = async (croppedFile) => {
    try {
      setUploadingPicture(true);
      const res = await uploadImage(croppedFile);
      const imageUrl = res.data?.url || res.data?.data?.url || res.url;
      if (!imageUrl) {
        console.error("No URL in response:", res);
        throw new Error("No URL in response");
      }
      setEditPicture(imageUrl);
      toast.success("Profile picture uploaded!");
    } catch (err) {
      console.error("Upload error:", err);
      // Handle various error formats safely
      let errorMessage = "Failed to upload image";
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleCoverUpload = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      
      // Validate file size (10MB max for cover)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Cover image must be less than 10MB");
        return;
      }
      
      setUploadingCover(true);
      const res = await uploadImage(file);
      const imageUrl = res.data?.url || res.data?.data?.url || res.url;
      if (!imageUrl) {
        console.error("No URL in response:", res);
        throw new Error("No URL in response");
      }
      setEditCoverImage(imageUrl);
      toast.success("Cover image uploaded!");
    } catch (err) {
      console.error("Upload error:", err);
      // Handle various error formats safely
      let errorMessage = "Failed to upload image";
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    } finally {
      setUploadingCover(false);
    }
  };

  const handleEdit = async () => {
    try {
      const res = await updateProfile({ 
        name: editName, 
        bio: editBio, 
        language: editLanguage,
        religious_role: editReligiousRole,
        relationship_status: editRelationshipStatus,
        confirmation_saint: editConfirmationSaint,
        favorite_quote: editFavoriteQuote,
        social_links: editSocialLinks,
        picture: editPicture,
        cover_image: editCoverImage,
        saved_zip_code: editSavedZipCode,
        saved_country: editSavedCountry,
        gender: editGender,
        birth_date: editBirthDate
      });
      setProfile({ ...profile, ...res.data });
      localStorage.setItem("user", JSON.stringify({ 
        ...currentUser, 
        name: editName, 
        bio: editBio, 
        language: editLanguage,
        religious_role: editReligiousRole,
        relationship_status: editRelationshipStatus,
        confirmation_saint: editConfirmationSaint,
        favorite_quote: editFavoriteQuote,
        social_links: editSocialLinks,
        picture: editPicture,
        cover_image: editCoverImage,
        saved_zip_code: editSavedZipCode,
        saved_country: editSavedCountry,
        gender: editGender,
        birth_date: editBirthDate
      }));
      setEditOpen(false);
      toast.success("Profile updated!");
      // Reload profile completeness
      if (isOwnProfile) {
        loadProfileCompleteness();
      }
    } catch {
      toast.error("Failed to update profile");
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    try {
      await sendMessage({ receiver_id: profileId, content: messageText });
      toast.success("Message sent!");
      setMessageOpen(false);
      setMessageText("");
      navigate("/messages");
    } catch {
      toast.error("Failed to send message");
    }
  };

  const handlePrivacyToggle = async (checked) => {
    try {
      await updateActivityPrivacy(checked);
      setActivityPublic(checked);
      toast.success(checked ? "Activity is now public" : "Activity is now private");
    } catch {
      toast.error("Failed to update privacy");
    }
  };

  // Offers & Requests handlers
  const handleReqPray = async (requestId) => {
    try {
      const res = await togglePrayRequest(requestId);
      setUserRequests(prev => prev.map(r =>
        r.request_id === requestId
          ? { ...r, user_prayed: res.data.is_praying, pray_count: res.data.pray_count }
          : r
      ));
    } catch {
      toast.error("Failed to update prayer");
    }
  };

  const handleReqDelete = async (requestId) => {
    if (!window.confirm("Delete this?")) return;
    try {
      await deleteRequest(requestId);
      setUserRequests(prev => prev.filter(r => r.request_id !== requestId));
      toast.success("Deleted successfully");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleReqShare = (listing) => {
    setReqShareTarget(listing);
    setShowReqShareDialog(true);
  };

  const handleReqEdit = (listing) => {
    setReqEditListing(listing);
    setReqEditTitle(listing.title || "");
    setReqEditContent(listing.content || "");
    setReqEditDialogOpen(true);
  };

  const handleReqSaveEdit = async () => {
    if (!reqEditListing || !reqEditTitle.trim() || !reqEditContent.trim()) return;
    setReqEditSaving(true);
    try {
      await updateRequest(reqEditListing.request_id, {
        title: reqEditTitle,
        content: reqEditContent
      });
      toast.success("Updated successfully");
      setReqEditDialogOpen(false);
      setReqEditListing(null);
      // Refresh data
      const reqRes = await getUserRequests(userId);
      setUserRequests(reqRes.data.requests || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setReqEditSaving(false);
  };

  const handleReqShareToUser = async (recipientUser) => {
    if (!reqShareTarget || !recipientUser) return;
    setReqSharing(true);
    try {
      const isOffer = reqShareTarget.listing_type === "offer";
      const link = `${window.location.origin}/requests?id=${reqShareTarget.request_id}`;
      const message = `Check out this ${isOffer ? 'offer' : 'request'}: "${reqShareTarget.title}"\n\n${link}`;
      await api.post("/messages/send", {
        receiver_id: recipientUser.user_id,
        content: message
      });
      toast.success(`Shared with ${recipientUser.name}`);
      setShowReqShareDialog(false);
      setReqShareTarget(null);
      setReqShareSearchQuery("");
    } catch {
      toast.error("Failed to share");
    }
    setReqSharing(false);
  };

  // Work History Handlers
  const handleSaveWork = async (data) => {
    setSavingWork(true);
    try {
      if (editingWork) {
        // Update existing
        await updateWorkEntry(editingWork.entry_id, data);
        setWorkHistory(prev => prev.map(w => 
          w.entry_id === editingWork.entry_id ? { ...w, ...data } : w
        ));
        toast.success("Work experience updated");
      } else {
        // Add new
        const res = await addWorkEntry(data);
        setWorkHistory(prev => [...prev, res.data.entry]);
        toast.success("Work experience added");
      }
      setWorkModalOpen(false);
      setEditingWork(null);
    } catch {
      toast.error("Failed to save work experience");
    }
    setSavingWork(false);
  };

  const handleDeleteWork = async (entryId) => {
    if (!window.confirm("Remove this work experience?")) return;
    try {
      await deleteWorkEntry(entryId);
      setWorkHistory(prev => prev.filter(w => w.entry_id !== entryId));
      toast.success("Work experience removed");
    } catch {
      toast.error("Failed to remove work experience");
    }
  };

  // Education History Handlers
  const handleSaveEducation = async (data) => {
    setSavingEducation(true);
    try {
      if (editingEducation) {
        // Update existing
        await updateEducationEntry(editingEducation.entry_id, data);
        setEducationHistory(prev => prev.map(e => 
          e.entry_id === editingEducation.entry_id ? { ...e, ...data } : e
        ));
        toast.success("Education updated");
      } else {
        // Add new
        const res = await addEducationEntry(data);
        setEducationHistory(prev => [...prev, res.data.entry]);
        toast.success("Education added");
      }
      setEducationModalOpen(false);
      setEditingEducation(null);
    } catch {
      toast.error("Failed to save education");
    }
    setSavingEducation(false);
  };

  const handleDeleteEducation = async (entryId) => {
    if (!window.confirm("Remove this education entry?")) return;
    try {
      await deleteEducationEntry(entryId);
      setEducationHistory(prev => prev.filter(e => e.entry_id !== entryId));
      toast.success("Education removed");
    } catch {
      toast.error("Failed to remove education");
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully!");
      setPasswordOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setChangingPassword(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
      <div className="w-8 h-8 border-2 border-[var(--liturgical-primary)] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  
  if (!profile) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
      <p className="text-[#6B665E] font-['Nunito']">User not found</p>
    </div>
  );

  const hasRating = profile.review_count > 0;
  const coverImage = profile.cover_image || profile.user?.cover_image;

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="profile-page">
      {/* Hero Cover Section */}
      <div className="relative h-56 md:h-72 bg-gradient-to-br from-[var(--liturgical-primary)] via-[var(--liturgical-primary-hover)] to-[var(--liturgical-text)] overflow-hidden">
        {/* Custom Cover Image or Default Pattern */}
        {coverImage ? (
          <img 
            src={resolveImageUrl(coverImage)} 
            alt="Cover" 
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          /* Decorative Pattern Overlay for default */
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D2A26]/60 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Profile Header Card - Overlapping Cover */}
        <div className="relative -mt-20 mb-8" data-testid="profile-header-card">
          <Card className="bg-white/95 backdrop-blur-sm border-[#E5E0D5] shadow-lg overflow-visible">
            <CardContent className="pt-4 pb-6 px-4 sm:px-6 md:px-8">
              {/* Avatar - Overlapping */}
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 -mt-16 sm:-mt-20">
                <div className="relative flex-shrink-0">
                  <Avatar className="h-28 w-28 sm:h-32 sm:w-32 md:h-40 md:w-40 border-4 border-white shadow-xl ring-4 ring-[var(--liturgical-primary)]/20">
                    <AvatarImage src={resolveImageUrl(profile.user?.picture || profile.picture)} className="object-cover" />
                    <AvatarFallback className="bg-gradient-to-br from-[var(--liturgical-primary)] to-[var(--liturgical-primary-hover)] text-white text-4xl md:text-5xl font-['Crimson_Text']">
                      {(profile.user?.name || profile.name)?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {/* Religious Role Badge on Avatar */}
                  {(profile.religious_role_info || profile.user?.religious_role_info) && (
                    <div 
                      className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-['Nunito'] font-semibold shadow-md whitespace-nowrap"
                      style={{ 
                        backgroundColor: (profile.religious_role_info || profile.user?.religious_role_info)?.color,
                        color: '#FFFFFF'
                      }}
                      data-testid="religious-role-badge"
                    >
                      {(profile.religious_role_info || profile.user?.religious_role_info)?.name}
                    </div>
                  )}
                </div>
                
                {/* Name & Actions Row */}
                <div className="flex-1 min-w-0 pt-0 sm:pt-12 md:pt-14">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0">
                      <h1 className="font-['Crimson_Text'] text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D2A26] tracking-tight break-words" data-testid="profile-name">
                        {profile.name || profile.user?.name}
                      </h1>
                      
                      {(profile.parish_name || profile.user?.parish_name) && (
                        <p className="text-base text-[var(--liturgical-primary)] font-['Nunito'] font-medium flex items-center gap-2 mt-1">
                          <Church className="w-4 h-4" />{profile.parish_name || profile.user?.parish_name}
                        </p>
                      )}
                      
                      {/* Seller Rating */}
                      {hasRating && (
                        <div className="flex items-center gap-2 mt-2" data-testid="seller-rating">
                          <StarRating rating={Math.round(profile.seller_rating)} />
                          <span className="text-sm font-['Nunito'] font-semibold text-[#2D2A26]">
                            {profile.seller_rating.toFixed(1)}
                          </span>
                          <span className="text-xs text-[#6B665E] font-['Nunito']">
                            ({profile.review_count} {profile.review_count === 1 ? "review" : "reviews"})
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2 flex-shrink-0">
                      {isOwnProfile ? (
                        <>
                        <Dialog open={editOpen} onOpenChange={setEditOpen}>
                          <DialogTrigger asChild>
                            <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold shadow-md" data-testid="edit-profile-btn">
                              <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
                            </Button>
                          </DialogTrigger>
                        <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[85vh]">
                          <DialogHeader>
                            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">Edit Profile</DialogTitle>
                          </DialogHeader>
                          <ScrollArea className="max-h-[65vh] pr-4">
                            <div className="space-y-4">
                              {/* Profile Picture & Cover Image Section */}
                              <div className="space-y-4 pb-4 border-b border-[#E5E0D5]">
                                {/* Cover Image Upload */}
                                <div className="space-y-2">
                                  <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                    <ImagePlus className="w-3.5 h-3.5 text-[var(--liturgical-primary)]" /> Cover Image
                                  </label>
                                  <div className="relative h-24 rounded-lg overflow-hidden bg-gradient-to-br from-[var(--liturgical-primary)] via-[var(--liturgical-primary-hover)] to-[var(--liturgical-text)]">
                                    {editCoverImage && (
                                      <img 
                                        src={resolveImageUrl(editCoverImage)} 
                                        alt="Cover" 
                                        className="w-full h-full object-cover"
                                      />
                                    )}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity">
                                      <label className="cursor-pointer">
                                        <input 
                                          type="file" 
                                          accept="image/*" 
                                          className="hidden" 
                                          onChange={handleCoverUpload}
                                          disabled={uploadingCover}
                                          data-testid="cover-image-input"
                                        />
                                        <div className="flex items-center gap-2 px-3 py-2 bg-white/90 rounded-lg text-sm font-['Nunito'] text-[#2D2A26]">
                                          {uploadingCover ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <Camera className="w-4 h-4" />
                                          )}
                                          {editCoverImage ? "Change Cover" : "Add Cover"}
                                        </div>
                                      </label>
                                    </div>
                                    {editCoverImage && (
                                      <button 
                                        onClick={() => setEditCoverImage("")}
                                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Profile Picture Upload */}
                                <div className="space-y-2">
                                  <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                    <Camera className="w-3.5 h-3.5 text-[var(--liturgical-primary)]" /> Profile Picture
                                  </label>
                                  <div className="flex items-center gap-4">
                                    <div className="relative">
                                      <Avatar className="h-20 w-20 border-2 border-[#E5E0D5]">
                                        <AvatarImage src={resolveImageUrl(editPicture)} className="object-cover" />
                                        <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xl font-['Crimson_Text']">
                                          {editName?.[0]?.toUpperCase() || profile?.name?.[0]?.toUpperCase()}
                                        </AvatarFallback>
                                      </Avatar>
                                      <label className="absolute -bottom-1 -right-1 cursor-pointer">
                                        <input 
                                          type="file" 
                                          accept="image/*" 
                                          className="hidden" 
                                          onChange={handlePictureUpload}
                                          disabled={uploadingPicture}
                                          data-testid="profile-picture-input"
                                        />
                                        <div className="w-7 h-7 bg-[var(--liturgical-primary)] hover:brightness-90 rounded-full flex items-center justify-center shadow-md">
                                          {uploadingPicture ? (
                                            <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                                          ) : (
                                            <Camera className="w-3.5 h-3.5 text-white" />
                                          )}
                                        </div>
                                      </label>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-xs text-[#6B665E] font-['Nunito']">
                                        Click the camera icon to upload a new profile picture.
                                      </p>
                                      {editPicture && (
                                        <div className="flex items-center gap-3 mt-1">
                                          <button 
                                            onClick={() => {
                                              setCropperImage(resolveImageUrl(editPicture));
                                              setCropperOpen(true);
                                            }}
                                            className="text-xs text-[var(--liturgical-primary)] hover:underline font-['Nunito'] flex items-center gap-1"
                                            data-testid="edit-profile-photo-btn"
                                          >
                                            <Pencil className="w-3 h-3" /> Edit / Crop
                                          </button>
                                          <button 
                                            onClick={() => setEditPicture("")}
                                            className="text-xs text-red-500 hover:text-red-600 font-['Nunito'] flex items-center gap-1"
                                          >
                                            <Trash2 className="w-3 h-3" /> Remove
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Basic Info Section */}
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26]">Name</label>
                                <Input value={editName} onChange={e => setEditName(e.target.value)} className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edit-name-input" />
                              </div>
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26]">Bio</label>
                                <Textarea value={editBio} onChange={e => setEditBio(e.target.value)} placeholder="Tell the community about yourself..." className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edit-bio-input" />
                              </div>
                              
                              {/* Relationship Status */}
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                  <Heart className="w-3.5 h-3.5 text-[var(--liturgical-primary)]" /> Relationship Status
                                </label>
                                <Select value={editRelationshipStatus || "none"} onValueChange={(v) => setEditRelationshipStatus(v === "none" ? "" : v)}>
                                  <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edit-relationship-status">
                                    <SelectValue placeholder="Select status (optional)" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white max-h-60">
                                    <SelectItem value="none" className="font-['Nunito'] text-sm text-[#6B665E]">Prefer not to say</SelectItem>
                                    {availableRelationshipStatuses.map(status => (
                                      <SelectItem key={status.id} value={status.id} className="font-['Nunito'] text-sm">{status.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Gender */}
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                  <Users className="w-3.5 h-3.5 text-[var(--liturgical-primary)]" /> Gender
                                </label>
                                <Select value={editGender || "none"} onValueChange={(v) => setEditGender(v === "none" ? "" : v)}>
                                  <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edit-gender">
                                    <SelectValue placeholder="Select gender (optional)" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white">
                                    <SelectItem value="none" className="font-['Nunito'] text-sm text-[#6B665E]">Prefer not to say</SelectItem>
                                    <SelectItem value="male" className="font-['Nunito'] text-sm">Male</SelectItem>
                                    <SelectItem value="female" className="font-['Nunito'] text-sm">Female</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Birth Date */}
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5 text-[var(--liturgical-primary)]" /> Birth Date
                                </label>
                                <Input 
                                  type="date"
                                  value={editBirthDate ? editBirthDate.split('T')[0] : ""} 
                                  onChange={e => setEditBirthDate(e.target.value)} 
                                  className="border-[#E5E0D5] font-['Nunito'] text-sm" 
                                  data-testid="edit-birth-date"
                                  max={new Date().toISOString().split('T')[0]}
                                />
                                <p className="text-[10px] text-[#6B665E] font-['Nunito']">
                                  Used for age-based member filtering
                                </p>
                              </div>
                              
                              {/* Confirmation Saint */}
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                  <Sparkles className="w-3.5 h-3.5 text-[var(--liturgical-secondary)]" /> Confirmation Saint
                                </label>
                                <Input 
                                  value={editConfirmationSaint} 
                                  onChange={e => setEditConfirmationSaint(e.target.value)} 
                                  placeholder="e.g., St. Thomas Aquinas"
                                  className="border-[#E5E0D5] font-['Nunito'] text-sm" 
                                  data-testid="edit-confirmation-saint"
                                />
                                <p className="text-[10px] text-[#6B665E] font-['Nunito']">
                                  The saint you chose at Confirmation
                                </p>
                              </div>
                              
                              {/* Favorite Quote */}
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                  <Quote className="w-3.5 h-3.5 text-[var(--liturgical-primary)]" /> Favorite Quote
                                </label>
                                <Textarea 
                                  value={editFavoriteQuote} 
                                  onChange={e => setEditFavoriteQuote(e.target.value)} 
                                  placeholder="A scripture verse, saint quote, or saying that inspires you..."
                                  className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[80px]" 
                                  data-testid="edit-favorite-quote"
                                />
                              </div>
                              
                              {/* Language Selection */}
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                  <Languages className="w-3.5 h-3.5 text-[#6B665E]" /> Preferred Language
                                </label>
                                <Select value={editLanguage} onValueChange={setEditLanguage}>
                                  <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edit-language-select">
                                    <SelectValue placeholder="Select your language" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white max-h-60">
                                    {availableLanguages.map(lang => (
                                      <SelectItem key={lang} value={lang} className="font-['Nunito'] text-sm">{lang}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Religious Role Badge Selection */}
                              <div className="space-y-1.5">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] flex items-center gap-1.5">
                                  <Cross className="w-3.5 h-3.5 text-[#6B665E]" /> Religious Role
                                </label>
                                <Select value={editReligiousRole || "none"} onValueChange={(v) => setEditReligiousRole(v === "none" ? "" : v)}>
                                  <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="edit-religious-role-select">
                                    <SelectValue placeholder="Select your role (optional)" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white max-h-60">
                                    <SelectItem value="none" className="font-['Nunito'] text-sm text-[#6B665E]">No role selected</SelectItem>
                                    {availableReligiousRoles.map(role => (
                                      <SelectItem key={role.id} value={role.id} className="font-['Nunito'] text-sm">
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: role.color }} />
                                          {role.name}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Social Links Section */}
                              <div className="space-y-3 pt-2 border-t border-[#E5E0D5]">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium flex items-center gap-1.5">
                                  <Globe className="w-3.5 h-3.5 text-[#6B665E]" /> Social Links
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <label className="font-['Nunito'] text-[10px] text-[#6B665E] flex items-center gap-1">
                                      <Facebook className="w-3 h-3" /> Facebook
                                    </label>
                                    <Input 
                                      value={editSocialLinks.facebook || ""} 
                                      onChange={e => setEditSocialLinks({...editSocialLinks, facebook: e.target.value})}
                                      placeholder="facebook.com/..."
                                      className="border-[#E5E0D5] font-['Nunito'] text-xs h-8"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="font-['Nunito'] text-[10px] text-[#6B665E] flex items-center gap-1">
                                      <Instagram className="w-3 h-3" /> Instagram
                                    </label>
                                    <Input 
                                      value={editSocialLinks.instagram || ""} 
                                      onChange={e => setEditSocialLinks({...editSocialLinks, instagram: e.target.value})}
                                      placeholder="@username"
                                      className="border-[#E5E0D5] font-['Nunito'] text-xs h-8"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="font-['Nunito'] text-[10px] text-[#6B665E] flex items-center gap-1">
                                      <Twitter className="w-3 h-3" /> X/Twitter
                                    </label>
                                    <Input 
                                      value={editSocialLinks.twitter || ""} 
                                      onChange={e => setEditSocialLinks({...editSocialLinks, twitter: e.target.value})}
                                      placeholder="@username"
                                      className="border-[#E5E0D5] font-['Nunito'] text-xs h-8"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="font-['Nunito'] text-[10px] text-[#6B665E] flex items-center gap-1">
                                      <Linkedin className="w-3 h-3" /> LinkedIn
                                    </label>
                                    <Input 
                                      value={editSocialLinks.linkedin || ""} 
                                      onChange={e => setEditSocialLinks({...editSocialLinks, linkedin: e.target.value})}
                                      placeholder="linkedin.com/in/..."
                                      className="border-[#E5E0D5] font-['Nunito'] text-xs h-8"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="font-['Nunito'] text-[10px] text-[#6B665E] flex items-center gap-1">
                                      <Youtube className="w-3 h-3" /> YouTube
                                    </label>
                                    <Input 
                                      value={editSocialLinks.youtube || ""} 
                                      onChange={e => setEditSocialLinks({...editSocialLinks, youtube: e.target.value})}
                                      placeholder="youtube.com/..."
                                      className="border-[#E5E0D5] font-['Nunito'] text-xs h-8"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="font-['Nunito'] text-[10px] text-[#6B665E] flex items-center gap-1">
                                      <ExternalLink className="w-3 h-3" /> Website
                                    </label>
                                    <Input 
                                      value={editSocialLinks.website || ""} 
                                      onChange={e => setEditSocialLinks({...editSocialLinks, website: e.target.value})}
                                      placeholder="yoursite.com"
                                      className="border-[#E5E0D5] font-['Nunito'] text-xs h-8"
                                    />
                                  </div>
                                </div>
                              </div>
                              
                              {/* Saved Location for Marketplace */}
                              <div className="space-y-3 pt-2 border-t border-[#E5E0D5]">
                                <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium flex items-center gap-1.5">
                                  <MapPin className="w-3.5 h-3.5 text-[#6B665E]" /> Marketplace Location
                                </label>
                                <p className="text-xs text-[#6B665E] font-['Nunito']">
                                  Save your location to auto-fill distance filters in the Marketplace.
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <label className="font-['Nunito'] text-[10px] text-[#6B665E]">Zip/Postal Code</label>
                                    <Input 
                                      value={editSavedZipCode || ""} 
                                      onChange={e => setEditSavedZipCode(e.target.value)}
                                      placeholder="e.g. 78701"
                                      className="border-[#E5E0D5] font-['Nunito'] text-xs h-8"
                                      data-testid="profile-saved-zip"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="font-['Nunito'] text-[10px] text-[#6B665E]">Country</label>
                                    <Select value={editSavedCountry || "us"} onValueChange={setEditSavedCountry}>
                                      <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-xs h-8" data-testid="profile-saved-country">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className="bg-white border-[#E5E0D5] max-h-48">
                                        <SelectItem value="us" className="font-['Nunito'] text-xs">United States</SelectItem>
                                        <SelectItem value="ca" className="font-['Nunito'] text-xs">Canada</SelectItem>
                                        <SelectItem value="gb" className="font-['Nunito'] text-xs">United Kingdom</SelectItem>
                                        <SelectItem value="au" className="font-['Nunito'] text-xs">Australia</SelectItem>
                                        <SelectItem value="ie" className="font-['Nunito'] text-xs">Ireland</SelectItem>
                                        <SelectItem value="ph" className="font-['Nunito'] text-xs">Philippines</SelectItem>
                                        <SelectItem value="mx" className="font-['Nunito'] text-xs">Mexico</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Activity Privacy Toggle */}
                              <div className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg">
                                <div className="flex items-center gap-2">
                                  {activityPublic ? <Eye className="w-4 h-4 text-[var(--liturgical-primary)]" /> : <EyeOff className="w-4 h-4 text-[#6B665E]" />}
                                  <div>
                                    <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Public Activity</Label>
                                    <p className="text-xs text-[#6B665E] font-['Nunito']">
                                      {activityPublic ? "Others can see your activity" : "Activity is private"}
                                    </p>
                                  </div>
                                </div>
                                <Switch
                                  checked={activityPublic}
                                  onCheckedChange={handlePrivacyToggle}
                                  data-testid="activity-privacy-toggle"
                                />
                              </div>
                              
                              <Button onClick={handleEdit} className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold" data-testid="save-profile-btn">
                                Save Changes
                              </Button>
                            </div>
                          </ScrollArea>
                        </DialogContent>
                      </Dialog>
                      
                      {/* Change Password Button */}
                      <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="bg-white border-[#E5E0D5] text-[#6B665E] hover:bg-[#F0EBE0] font-['Nunito']" data-testid="change-password-btn">
                            <Lock className="w-4 h-4 mr-2" /> Change Password
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
                          <DialogHeader>
                            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                              <Lock className="w-5 h-5 text-[var(--liturgical-primary)]" /> Change Password
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-2">
                            <div className="space-y-2">
                              <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Current Password</Label>
                              <Input 
                                type="password" 
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Enter current password"
                                className="border-[#E5E0D5] font-['Nunito']"
                                data-testid="current-password-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-['Nunito'] text-sm text-[#2D2A26]">New Password</Label>
                              <Input 
                                type="password" 
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password (min 8 characters)"
                                className="border-[#E5E0D5] font-['Nunito']"
                                data-testid="new-password-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Confirm New Password</Label>
                              <Input 
                                type="password" 
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                className="border-[#E5E0D5] font-['Nunito']"
                                data-testid="confirm-password-input"
                              />
                            </div>
                            <Button 
                              onClick={handleChangePassword} 
                              disabled={changingPassword}
                              className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold"
                              data-testid="submit-password-change-btn"
                            >
                              {changingPassword ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                              ) : (
                                "Change Password"
                              )}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      </>
                    ) : profile?.is_system_account ? (
                      <Badge className="bg-[#1B3A5C] text-white font-['Nunito'] text-xs px-3 py-1">
                        <Globe className="w-3 h-3 mr-1.5" /> News Source
                      </Badge>
                    ) : (
                      <>
                        {/* Friend Button */}
                        {(() => {
                          const config = getFriendButtonConfig();
                          const Icon = config.icon;
                          return (
                            <Button
                              size="sm"
                              variant={config.variant}
                              onClick={handleFriendAction}
                              disabled={friendLoading}
                              className={`font-['Nunito'] text-xs ${config.className}`}
                              data-testid="friend-action-btn"
                            >
                              {friendLoading ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Icon className="w-3 h-3 mr-1" />
                              )}
                              {config.label}
                            </Button>
                          );
                        })()}
                        
                        {/* Follow Button */}
                        <Button
                          size="sm"
                          variant={isFollowing ? "outline" : "ghost"}
                          onClick={handleFollowAction}
                          disabled={followLoading}
                          className={`font-['Nunito'] text-xs ${isFollowing ? 'border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)]' : 'text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5'}`}
                          data-testid="follow-action-btn"
                          title={isFollowing ? "Unfollow to stop getting notifications" : "Follow to get notified when they post"}
                        >
                          {followLoading ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : isFollowing ? (
                            <BellOff className="w-3 h-3 mr-1" />
                          ) : (
                            <Bell className="w-3 h-3 mr-1" />
                          )}
                          {isFollowing ? "Following" : "Follow"}
                        </Button>
                        
                        <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
                          <DialogTrigger asChild>
                            <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold shadow-md" data-testid="send-message-btn">
                              <MessageCircle className="w-4 h-4 mr-2" /> Message
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-white border-[#E5E0D5]">
                            <DialogHeader>
                              <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
                                Send Message to {profile.name}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                              <Textarea value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Write your message..." className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[80px]" data-testid="profile-message-input" />
                              <Button onClick={handleSendMessage} className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="profile-send-message-btn">
                                Send
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Stats Bar - Full Width */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-6 border-t border-[#E5E0D5]">
                <button 
                  onClick={() => setActiveTab("friends")}
                  className="group p-4 rounded-xl bg-gradient-to-br from-[#F9F7F2] to-[#F0EBE0] hover:from-[var(--liturgical-primary)]/5 hover:to-[var(--liturgical-primary)]/10 transition-all duration-300"
                >
                  <div className="flex flex-col items-center">
                    <Users className="w-5 h-5 text-[var(--liturgical-primary)] mb-1 group-hover:scale-110 transition-transform" />
                    <span className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26]">{userFriends.length}</span>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] font-medium">Friends</p>
                  </div>
                </button>
                <div className="p-4 rounded-xl bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5">
                  <div className="flex flex-col items-center">
                    <Home className="w-5 h-5 text-[var(--liturgical-secondary)] mb-1" />
                    <span className="font-['Crimson_Text'] text-2xl font-bold text-[var(--liturgical-secondary)]">{userFamily.length}</span>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] font-medium">Family</p>
                  </div>
                </div>
                <button 
                  onClick={() => setActiveTab("posts")}
                  className="group p-4 rounded-xl bg-gradient-to-br from-[#F9F7F2] to-[#F0EBE0] hover:from-[#556B2F]/5 hover:to-[#556B2F]/10 transition-all duration-300"
                >
                  <div className="flex flex-col items-center">
                    <FileText className="w-5 h-5 text-[var(--liturgical-primary)] mb-1 group-hover:scale-110 transition-transform" />
                    <span className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26]">{profile.posts?.length || 0}</span>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] font-medium">Posts</p>
                  </div>
                </button>
                <button 
                  onClick={() => setActiveTab("listings")}
                  className="group p-4 rounded-xl bg-gradient-to-br from-[#F9F7F2] to-[#F0EBE0] hover:from-[var(--liturgical-primary)]/5 hover:to-[var(--liturgical-primary)]/10 transition-all duration-300"
                >
                  <div className="flex flex-col items-center">
                    <ShoppingBag className="w-5 h-5 text-[var(--liturgical-primary)] mb-1 group-hover:scale-110 transition-transform" />
                    <span className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26]">{(profile.listings?.length || 0) + (profile.business_listings_count || 0)}</span>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] font-medium">Listings</p>
                  </div>
                </button>
              </div>
              
              {/* Relationship Badges (for other profiles) */}
              {!isOwnProfile && (friendStatus.status === "friends" || familyStatus.status === "family" || mutualFriends.length > 0) && (
                <div className="flex gap-2 mt-4 flex-wrap justify-center">
                  {friendStatus.status === "friends" && (
                    <Badge className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] px-4 py-1.5 text-sm">
                      <Users className="w-4 h-4 mr-1.5" /> Friends
                    </Badge>
                  )}
                  {familyStatus.status === "family" && (
                    <Badge className="bg-[var(--liturgical-secondary)] text-white font-['Nunito'] px-4 py-1.5 text-sm">
                      <Home className="w-4 h-4 mr-1.5" /> {familyStatus.relationship_label || "Family"}
                    </Badge>
                  )}
                  {mutualFriends.length > 0 && (
                    <Badge variant="outline" className="border-[#6B665E] text-[#6B665E] font-['Nunito'] px-4 py-1.5 text-sm">
                      {mutualFriends.length} mutual {mutualFriends.length === 1 ? "friend" : "friends"}
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="profile-tabs">
          <TabsList className="bg-white border border-[#E5E0D5] mb-8 flex-wrap h-auto p-1.5 rounded-xl shadow-sm w-full justify-start gap-1">
            <TabsTrigger value="about" className="font-['Nunito'] text-sm font-medium rounded-lg px-4 py-2 data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" data-testid="about-tab">
              About
            </TabsTrigger>
            <TabsTrigger value="posts" className="font-['Nunito'] text-sm font-medium rounded-lg px-4 py-2 data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all">
              Posts
            </TabsTrigger>
            <TabsTrigger value="media" className="font-['Nunito'] text-sm font-medium rounded-lg px-4 py-2 data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" data-testid="media-tab">
              Media
            </TabsTrigger>
            <TabsTrigger value="friends" className="font-['Nunito'] text-sm font-medium rounded-lg px-4 py-2 data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" data-testid="friends-tab">
              Friends
            </TabsTrigger>
            <TabsTrigger value="listings" className="font-['Nunito'] text-sm font-medium rounded-lg px-4 py-2 data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all">
              Listings
            </TabsTrigger>
            <TabsTrigger value="offers" className="font-['Nunito'] text-sm font-medium rounded-lg px-4 py-2 data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" data-testid="offers-tab">
              Offers & Requests
            </TabsTrigger>
            <TabsTrigger value="reviews" className="font-['Nunito'] text-sm font-medium rounded-lg px-4 py-2 data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" data-testid="reviews-tab">
              Reviews
            </TabsTrigger>
            <TabsTrigger value="activity" className="font-['Nunito'] text-sm font-medium rounded-lg px-4 py-2 data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all" data-testid="activity-tab">
              Activity
            </TabsTrigger>
          </TabsList>

          {/* About Tab */}
          <TabsContent value="about" data-testid="about-content">
            {/* Profile Completeness Banner */}
            {isOwnProfile && profileCompleteness && !profileCompleteness.is_complete && (
              <Card className="bg-gradient-to-r from-[var(--liturgical-primary)]/5 to-[#D4AF37]/5 border-[var(--liturgical-secondary)]/30 mb-6 shadow-sm" data-testid="profile-completeness-banner">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C4A02F] flex items-center justify-center flex-shrink-0 shadow-md">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26]">Complete Your Profile</h3>
                      <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
                        Help the community get to know you better! Your profile is {profileCompleteness.percentage}% complete.
                      </p>
                      <Progress value={profileCompleteness.percentage} className="mt-3 h-2.5 bg-[#E5E0D5]" />
                      <div className="flex flex-wrap gap-2 mt-3">
                        {profileCompleteness.incomplete_items?.slice(0, 3).map(item => (
                          <Badge key={item.key} variant="outline" className="border-[var(--liturgical-secondary)]/50 text-[#6B665E] font-['Nunito'] text-xs">
                            {item.message}
                          </Badge>
                        ))}
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => setEditOpen(true)}
                        className="mt-3 bg-[var(--liturgical-secondary)] hover:brightness-90 text-white font-['Nunito']"
                      >
                        <Edit2 className="w-3 h-3 mr-1" /> Complete Profile
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Favorite Quote - Featured Section */}
            {(profile.user?.favorite_quote || profile.favorite_quote) && (
              <Card className="bg-gradient-to-br from-[#556B2F]/5 to-[#556B2F]/10 border-[var(--liturgical-primary)]/20 mb-6 shadow-sm overflow-hidden">
                <CardContent className="p-6 relative">
                  <Quote className="absolute top-4 left-4 w-12 h-12 text-[var(--liturgical-primary)]/20" />
                  <Quote className="absolute bottom-4 right-4 w-12 h-12 text-[var(--liturgical-primary)]/20 rotate-180" />
                  <div className="relative z-10 text-center px-8 py-4">
                    <p className="text-lg md:text-xl text-[#2D2A26] font-['Crimson_Text'] italic leading-relaxed">
                      "{profile.user?.favorite_quote || profile.favorite_quote}"
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Social Links - Horizontal Bar */}
            {(() => {
              const links = profile.user?.social_links || profile.social_links || {};
              const hasLinks = Object.values(links).some(v => v);
              if (!hasLinks) return null;
              return (
                <Card className="bg-white border-[#E5E0D5] mb-6 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      {links.facebook && (
                        <a href={links.facebook.startsWith('http') ? links.facebook : `https://facebook.com/${links.facebook}`} target="_blank" rel="noopener noreferrer" 
                           className="w-11 h-11 flex items-center justify-center bg-[#1877F2] hover:bg-[#1665D8] rounded-full transition-all hover:scale-110 shadow-md">
                          <Facebook className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {links.instagram && (
                        <a href={links.instagram.startsWith('http') ? links.instagram : `https://instagram.com/${links.instagram}`} target="_blank" rel="noopener noreferrer" 
                           className="w-11 h-11 flex items-center justify-center bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 rounded-full transition-all hover:scale-110 shadow-md">
                          <Instagram className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {links.twitter && (
                        <a href={links.twitter.startsWith('http') ? links.twitter : `https://x.com/${links.twitter}`} target="_blank" rel="noopener noreferrer" 
                           className="w-11 h-11 flex items-center justify-center bg-[#000000] hover:bg-[#333333] rounded-full transition-all hover:scale-110 shadow-md">
                          <Twitter className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {links.linkedin && (
                        <a href={links.linkedin.startsWith('http') ? links.linkedin : `https://linkedin.com/in/${links.linkedin}`} target="_blank" rel="noopener noreferrer" 
                           className="w-11 h-11 flex items-center justify-center bg-[#0A66C2] hover:bg-[#0855A5] rounded-full transition-all hover:scale-110 shadow-md">
                          <Linkedin className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {links.youtube && (
                        <a href={links.youtube.startsWith('http') ? links.youtube : `https://youtube.com/${links.youtube}`} target="_blank" rel="noopener noreferrer" 
                           className="w-11 h-11 flex items-center justify-center bg-[#FF0000] hover:bg-[#CC0000] rounded-full transition-all hover:scale-110 shadow-md">
                          <Youtube className="w-5 h-5 text-white" />
                        </a>
                      )}
                      {links.website && (
                        <a href={links.website.startsWith('http') ? links.website : `https://${links.website}`} target="_blank" rel="noopener noreferrer" 
                           className="w-11 h-11 flex items-center justify-center bg-[var(--liturgical-primary)] hover:brightness-90 rounded-full transition-all hover:scale-110 shadow-md">
                          <Globe className="w-5 h-5 text-white" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Basic Info Card */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm">
                <CardHeader className="pb-3 border-b border-[#E5E0D5]/50">
                  <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                      <Users className="w-4 h-4 text-[var(--liturgical-primary)]" />
                    </div>
                    About
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {(profile.bio || profile.user?.bio) && (
                    <div className="p-4 bg-[#F9F7F2] rounded-lg">
                      <p className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed">{profile.bio || profile.user?.bio}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    {(profile.parish_name || profile.user?.parish_name) && (
                      <div className="p-3 rounded-lg bg-[var(--liturgical-primary)]/5 border border-[var(--liturgical-primary)]/10">
                        <div className="flex items-center gap-2 mb-1">
                          <Church className="w-4 h-4 text-[var(--liturgical-primary)]" />
                          <p className="text-[10px] text-[#6B665E] font-['Nunito'] uppercase tracking-wide">Parish</p>
                        </div>
                        <p className="text-sm text-[#2D2A26] font-['Nunito'] font-semibold">{profile.parish_name || profile.user?.parish_name}</p>
                      </div>
                    )}
                    
                    {(profile.language || profile.user?.language) && (
                      <div className="p-3 rounded-lg bg-[var(--liturgical-primary)]/5 border border-[var(--liturgical-primary)]/10">
                        <div className="flex items-center gap-2 mb-1">
                          <Languages className="w-4 h-4 text-[var(--liturgical-primary)]" />
                          <p className="text-[10px] text-[#6B665E] font-['Nunito'] uppercase tracking-wide">Language</p>
                        </div>
                        <p className="text-sm text-[#2D2A26] font-['Nunito'] font-semibold">{profile.language || profile.user?.language}</p>
                      </div>
                    )}
                    
                    {(profile.user?.relationship_status_info || profile.relationship_status_info) && (
                      <div className="p-3 rounded-lg bg-[var(--liturgical-secondary)]/5 border border-[var(--liturgical-secondary)]/10">
                        <div className="flex items-center gap-2 mb-1">
                          <Heart className="w-4 h-4 text-[var(--liturgical-secondary)]" />
                          <p className="text-[10px] text-[#6B665E] font-['Nunito'] uppercase tracking-wide">Status</p>
                        </div>
                        <p className="text-sm text-[#2D2A26] font-['Nunito'] font-semibold">{(profile.user?.relationship_status_info || profile.relationship_status_info)?.label}</p>
                      </div>
                    )}
                    
                    {(profile.user?.confirmation_saint || profile.confirmation_saint) && (
                      <div className="p-3 rounded-lg bg-[var(--liturgical-secondary)]/5 border border-[var(--liturgical-secondary)]/10">
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="w-4 h-4 text-[var(--liturgical-secondary)]" />
                          <p className="text-[10px] text-[#6B665E] font-['Nunito'] uppercase tracking-wide">Patron Saint</p>
                        </div>
                        <p className="text-sm text-[#2D2A26] font-['Nunito'] font-semibold">{profile.user?.confirmation_saint || profile.confirmation_saint}</p>
                      </div>
                    )}
                    
                    {(profile.created_at || profile.user?.created_at) && (
                      <div className="p-3 rounded-lg bg-[#F0EBE0]">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-[#6B665E]" />
                          <p className="text-[10px] text-[#6B665E] font-['Nunito'] uppercase tracking-wide">Member Since</p>
                        </div>
                        <p className="text-sm text-[#2D2A26] font-['Nunito'] font-semibold">{new Date(profile.created_at || profile.user?.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Family Card */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm">
                <CardHeader className="pb-3 border-b border-[#E5E0D5]/50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[var(--liturgical-secondary)]/10 flex items-center justify-center">
                        <Home className="w-4 h-4 text-[var(--liturgical-secondary)]" />
                      </div>
                      Family
                      <span className="text-sm text-[#6B665E] font-['Nunito'] font-normal">({userFamily.length})</span>
                    </CardTitle>
                    {isOwnProfile && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/family")}
                        className="text-[var(--liturgical-secondary)] hover:bg-[var(--liturgical-secondary)]/10 font-['Nunito'] text-xs"
                        data-testid="manage-family-btn"
                      >
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Manage
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {userFamily.length === 0 ? (
                    <div className="text-center py-6">
                      {isOwnProfile ? (
                        <div>
                          <div className="w-12 h-12 rounded-full bg-[var(--liturgical-secondary)]/10 flex items-center justify-center mx-auto mb-3">
                            <Home className="w-6 h-6 text-[var(--liturgical-secondary)]" />
                          </div>
                          <p className="text-sm text-[#6B665E] font-['Nunito'] mb-3">Connect with your family on Parish</p>
                          <Button
                            size="sm"
                            onClick={() => navigate("/family")}
                            className="bg-[var(--liturgical-secondary)] hover:brightness-90 text-white font-['Nunito']"
                            data-testid="add-family-profile-btn"
                          >
                            <UserPlus className="w-4 h-4 mr-1.5" /> Add Family Member
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-[#6B665E] font-['Nunito']">No family members listed</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {userFamily.slice(0, 5).map(member => (
                        <div 
                          key={member.user_id} 
                          className="flex items-center gap-3 p-2 hover:bg-[#F9F7F2] rounded-lg cursor-pointer transition-colors"
                          onClick={() => navigate(`/profile/${member.user_id}`)}
                        >
                          <Avatar className="h-10 w-10 border border-[var(--liturgical-secondary)]">
                            <AvatarImage src={resolveImageUrl(member.picture)} />
                            <AvatarFallback className="bg-[var(--liturgical-secondary)] text-white font-['Nunito'] text-sm">
                              {member.name?.[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito'] truncate">{member.name}</p>
                            <Badge className="bg-[var(--liturgical-secondary)]/10 text-[var(--liturgical-secondary)] text-[10px] border border-[var(--liturgical-secondary)]/30">
                              {member.relationship_label}
                            </Badge>
                          </div>
                        </div>
                      ))}
                      {userFamily.length > 5 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate("/family")}
                          className="w-full text-[var(--liturgical-secondary)] hover:bg-[var(--liturgical-secondary)]/10 font-['Nunito'] text-xs mt-2"
                        >
                          View all {userFamily.length} family members →
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Mutual Friends Card (only for other profiles) */}
              {!isOwnProfile && mutualFriends.length > 0 && (
                <Card className="bg-white border-[#E5E0D5] shadow-sm md:col-span-2">
                  <CardHeader className="pb-3 border-b border-[#E5E0D5]/50">
                    <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-[var(--liturgical-primary)]" />
                      </div>
                      Mutual Friends
                      <span className="text-sm text-[#6B665E] font-['Nunito'] font-normal">({mutualFriends.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="flex flex-wrap gap-3">
                      {mutualFriends.slice(0, 10).map(friend => (
                        <div 
                          key={friend.user_id}
                          className="flex items-center gap-2 p-2 bg-[#F9F7F2] rounded-lg cursor-pointer hover:bg-[#F0EBE0] transition-colors"
                          onClick={() => navigate(`/profile/${friend.user_id}`)}
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={resolveImageUrl(friend.picture)} />
                            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-xs">
                              {friend.name?.[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <p className="text-sm font-['Nunito'] text-[#2D2A26]">{friend.name}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Work & Education Row */}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Work History Card */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid="work-history-card">
                <CardHeader className="pb-3 border-b border-[#E5E0D5]/50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-[var(--liturgical-primary)]" />
                      </div>
                      Work
                      <span className="text-sm text-[#6B665E] font-['Nunito'] font-normal">({workHistory.length})</span>
                    </CardTitle>
                    {isOwnProfile && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditingWork(null); setWorkModalOpen(true); }}
                        className="text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                        data-testid="add-work-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {workHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 rounded-full bg-[#F0EBE0] flex items-center justify-center mx-auto mb-3">
                        <Briefcase className="w-7 h-7 text-[#6B665E]" />
                      </div>
                      <p className="text-sm text-[#6B665E] font-['Nunito']">
                        {isOwnProfile ? "Add your work experience" : "No work history listed"}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {workHistory.sort((a, b) => (b.current ? 1 : 0) - (a.current ? 1 : 0) || b.start_year - a.start_year).map(work => (
                        <div 
                          key={work.entry_id} 
                          className="p-3 bg-[#F9F7F2] rounded-lg group"
                          data-testid={`work-entry-${work.entry_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">{work.position}</p>
                              <p className="text-sm text-[#6B665E] font-['Nunito'] flex items-center gap-1 mt-0.5">
                                <Building2 className="w-3 h-3" /> {work.employer}
                              </p>
                              <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">
                                {work.start_year} - {work.current ? <span className="text-[var(--liturgical-primary)] font-medium">Present</span> : (work.end_year || "Present")}
                              </p>
                            </div>
                            {isOwnProfile && (
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => { setEditingWork(work); setWorkModalOpen(true); }}
                                  className="h-7 w-7 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)]"
                                  data-testid={`edit-work-${work.entry_id}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteWork(work.entry_id)}
                                  className="h-7 w-7 p-0 text-[#6B665E] hover:text-red-600"
                                  data-testid={`delete-work-${work.entry_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Education History Card */}
              <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid="education-history-card">
                <CardHeader className="pb-3 border-b border-[#E5E0D5]/50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-[var(--liturgical-primary)]" />
                      </div>
                      Education
                      <span className="text-sm text-[#6B665E] font-['Nunito'] font-normal">({educationHistory.length})</span>
                    </CardTitle>
                    {isOwnProfile && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditingEducation(null); setEducationModalOpen(true); }}
                        className="text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                        data-testid="add-education-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {educationHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 rounded-full bg-[#F0EBE0] flex items-center justify-center mx-auto mb-3">
                        <GraduationCap className="w-7 h-7 text-[#6B665E]" />
                      </div>
                      <p className="text-sm text-[#6B665E] font-['Nunito']">
                        {isOwnProfile ? "Add your education" : "No education listed"}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {educationHistory.sort((a, b) => (b.current ? 1 : 0) - (a.current ? 1 : 0) || b.start_year - a.start_year).map(edu => (
                        <div 
                          key={edu.entry_id} 
                          className="p-3 bg-[#F9F7F2] rounded-lg group"
                          data-testid={`edu-entry-${edu.entry_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">{edu.institution}</p>
                              <p className="text-sm text-[#6B665E] font-['Nunito'] mt-0.5">
                                {edu.degree}{edu.field_of_study ? ` in ${edu.field_of_study}` : ""}
                              </p>
                              <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">
                                {edu.start_year} - {edu.current ? <span className="text-[var(--liturgical-primary)] font-medium">Present</span> : (edu.end_year || "Present")}
                              </p>
                            </div>
                            {isOwnProfile && (
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => { setEditingEducation(edu); setEducationModalOpen(true); }}
                                  className="h-7 w-7 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)]"
                                  data-testid={`edit-edu-${edu.entry_id}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteEducation(edu.entry_id)}
                                  className="h-7 w-7 p-0 text-[#6B665E] hover:text-red-600"
                                  data-testid={`delete-edu-${edu.entry_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Friends Tab */}
          <TabsContent value="friends" data-testid="friends-content">
            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-2">
                <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                  <Users className="w-4 h-4 text-[var(--liturgical-primary)]" /> Friends ({userFriends.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {userFriends.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                    <p className="text-sm text-[#6B665E] font-['Nunito']">No friends yet</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {userFriends.map(friend => (
                      <div 
                        key={friend.user_id}
                        className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg cursor-pointer hover:bg-[#F0EBE0] transition-colors"
                        onClick={() => navigate(`/profile/${friend.user_id}`)}
                        data-testid={`friend-card-${friend.user_id}`}
                      >
                        <Avatar className="h-12 w-12 border border-[var(--liturgical-primary)]">
                          <AvatarImage src={resolveImageUrl(friend.picture)} />
                          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito']">
                            {friend.name?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito'] truncate">{friend.name}</p>
                          {friend.parish_name && (
                            <p className="text-xs text-[#6B665E] font-['Nunito'] truncate flex items-center gap-1">
                              <Church className="w-3 h-3" /> {friend.parish_name}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="posts">
            <div className="space-y-4 max-w-2xl">
              {(profile.posts || []).length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                  <p className="text-sm text-[#6B665E] font-['Nunito']">No posts yet</p>
                </div>
              ) : (
                profile.posts.map(post => (
                  <PostCard 
                    key={post.post_id} 
                    post={post}
                    currentUserId={currentUser?.user_id}
                    onLike={async (postId) => {
                      const res = await likePost(postId);
                      setProfile(prev => ({
                        ...prev,
                        posts: prev.posts.map(p => p.post_id === postId ? {
                          ...p,
                          user_liked: res.data.liked,
                          like_count: res.data.like_count
                        } : p)
                      }));
                    }}
                    onComment={() => {}}
                    onEdit={async (postId, content) => {
                      const { updatePost } = await import("@/lib/api");
                      await updatePost(postId, { content });
                      loadProfile();
                    }}
                    onDelete={async (postId) => {
                      const { deletePost } = await import("@/lib/api");
                      await deletePost(postId);
                      setProfile(prev => ({
                        ...prev,
                        posts: prev.posts.filter(p => p.post_id !== postId)
                      }));
                    }}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Media Gallery Tab - Full width */}
          <TabsContent value="media" data-testid="media-content" className="w-full max-w-none">
            <Card className="bg-white border-[#E5E0D5] shadow-sm">
              <CardHeader className="border-b border-[#E5E0D5] pb-4">
                <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                  <Image className="w-5 h-5 text-[var(--liturgical-primary)]" />
                  Photos & Videos
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 sm:p-3">
                <MediaGallery userId={userId} isOwnProfile={isOwnProfile} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="listings">
            <ProfileListingsTab userId={userId} />
          </TabsContent>

          <TabsContent value="offers" data-testid="offers-content">
            <div className="space-y-4">
              {userRequests.length === 0 ? (
                <div className="text-center py-12">
                  <HandHeart className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                  <p className="text-sm text-[#6B665E] font-['Nunito']">No offers or requests yet</p>
                </div>
              ) : (
                userRequests.map(req => (
                  <ListingCard
                    key={req.request_id}
                    listing={req}
                    user={currentUser}
                    onPray={handleReqPray}
                    onDelete={handleReqDelete}
                    onShare={handleReqShare}
                    onEdit={handleReqEdit}
                  />
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsList reviews={reviews} />
          </TabsContent>

          <TabsContent value="activity">
            {isOwnProfile && (
              <div className="flex items-center justify-between mb-4 p-3 bg-white border border-[#E5E0D5] rounded-lg">
                <div className="flex items-center gap-2">
                  {activityPublic ? <Eye className="w-4 h-4 text-[var(--liturgical-primary)]" /> : <EyeOff className="w-4 h-4 text-[#6B665E]" />}
                  <span className="text-sm font-['Nunito'] text-[#2D2A26]">
                    {activityPublic ? "Your activity is public" : "Your activity is private"}
                  </span>
                </div>
                <Switch
                  checked={activityPublic}
                  onCheckedChange={handlePrivacyToggle}
                  data-testid="activity-privacy-toggle-inline"
                />
              </div>
            )}
            <ActivityFeed activities={activities} isOwnProfile={isOwnProfile} />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Work Entry Modal */}
      <WorkEntryModal
        isOpen={workModalOpen}
        onClose={() => { setWorkModalOpen(false); setEditingWork(null); }}
        onSave={handleSaveWork}
        entry={editingWork}
        isLoading={savingWork}
      />
      
      {/* Education Entry Modal */}
      <EducationEntryModal
        isOpen={educationModalOpen}
        onClose={() => { setEducationModalOpen(false); setEditingEducation(null); }}
        onSave={handleSaveEducation}
        entry={editingEducation}
        isLoading={savingEducation}
      />
      
      {/* Image Cropper Modal */}
      <ImageCropper
        open={cropperOpen}
        onOpenChange={setCropperOpen}
        imageSrc={cropperImage}
        onCropComplete={handleCroppedImage}
        aspect={1}
        circularCrop={true}
      />

      {/* Request Share Dialog */}
      <Dialog open={showReqShareDialog} onOpenChange={setShowReqShareDialog}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-lg">Share to Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search users..."
              value={reqShareSearchQuery}
              onChange={async (e) => {
                setReqShareSearchQuery(e.target.value);
                if (e.target.value.length >= 1) {
                  try {
                    const res = await searchUsers(e.target.value, { limit: 5 });
                    setReqShareResults(res.data?.users || res.data || []);
                  } catch { setReqShareResults([]); }
                } else {
                  setReqShareResults([]);
                }
              }}
              className="border-[#E5E0D5]"
              data-testid="req-share-search"
            />
            <div className="max-h-60 overflow-y-auto space-y-1">
              {reqShareResults.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => handleReqShareToUser(u)}
                  disabled={reqSharing}
                  className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-[#F9F7F2] transition-colors text-left"
                  data-testid={`req-share-user-${u.user_id}`}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={u.profile_picture} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">
                      {u.name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-['Nunito'] text-sm text-[#2D2A26]">{u.name}</span>
                  {reqSharing && <Loader2 className="w-4 h-4 ml-auto animate-spin" />}
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Edit Dialog */}
      <Dialog open={reqEditDialogOpen} onOpenChange={setReqEditDialogOpen}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-lg">Edit {reqEditListing?.listing_type === 'offer' ? 'Offer' : 'Request'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1 font-['Nunito']">Title</label>
              <Input
                value={reqEditTitle}
                onChange={(e) => setReqEditTitle(e.target.value)}
                className="border-[#E5E0D5]"
                data-testid="req-edit-title"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2A26] block mb-1 font-['Nunito']">Content</label>
              <Textarea
                value={reqEditContent}
                onChange={(e) => setReqEditContent(e.target.value)}
                className="border-[#E5E0D5] min-h-[100px]"
                data-testid="req-edit-content"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReqEditDialogOpen(false)} className="border-[#E5E0D5]">
                Cancel
              </Button>
              <Button
                onClick={handleReqSaveEdit}
                disabled={reqEditSaving || !reqEditTitle.trim() || !reqEditContent.trim()}
                className="bg-[var(--liturgical-primary)] hover:brightness-90"
                data-testid="req-edit-save-btn"
              >
                {reqEditSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ProfileListingsTab({ userId }) {
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState("marketplace");
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [businessListings, setBusinessListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [mRes, bRes] = await Promise.all([
          getListings({ seller_id: userId }),
          api.get(`/directory/user/${userId}`)
        ]);
        setMarketplaceListings(mRes.data?.listings || mRes.data || []);
        setBusinessListings(bRes.data?.listings || []);
      } catch (err) {
        console.error("Failed to load listings:", err);
      }
      setLoading(false);
    };
    load();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-[#F0EBE0] rounded-lg p-1">
        <button
          onClick={() => setSubTab("marketplace")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-['Nunito'] font-medium transition-colors ${
            subTab === "marketplace" ? "bg-white text-[var(--liturgical-primary)] shadow-sm" : "text-[#6B665E] hover:text-[#2D2A26]"
          }`}
          data-testid="listing-tab-marketplace"
        >
          <ShoppingBag className="w-4 h-4" />
          Items for Sale ({marketplaceListings.length})
        </button>
        <button
          onClick={() => setSubTab("business")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-['Nunito'] font-medium transition-colors ${
            subTab === "business" ? "bg-white text-[var(--liturgical-primary)] shadow-sm" : "text-[#6B665E] hover:text-[#2D2A26]"
          }`}
          data-testid="listing-tab-business"
        >
          <Store className="w-4 h-4" />
          Business Listings ({businessListings.length})
        </button>
      </div>

      {subTab === "marketplace" && (
        marketplaceListings.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
            <p className="text-sm text-[#6B665E] font-['Nunito']">No items for sale</p>
          </div>
        ) : (
          <div className="space-y-3">
            {marketplaceListings.map(listing => (
              <Card key={listing.listing_id} className="bg-white border-[#E5E0D5] shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/listing/${listing.listing_id}`)} data-testid={`marketplace-listing-${listing.listing_id}`}>
                <CardContent className="p-4 flex gap-4">
                  {(listing.image_url || listing.image_urls?.[0]) && (
                    <img src={resolveImageUrl(listing.image_url || listing.image_urls?.[0])} alt={listing.title} className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26] truncate">{listing.title}</h3>
                      <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] whitespace-nowrap">${listing.price}</span>
                    </div>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1 line-clamp-1">{listing.description?.replace(/<[^>]+>/g, '')}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[10px]"><Tag className="w-2.5 h-2.5 mr-1" />{listing.category}</Badge>
                      {listing.average_rating > 0 && <span className="flex items-center gap-0.5 text-[10px] text-[#D4AF37]"><Star className="w-3 h-3 fill-current" /> {listing.average_rating}</span>}
                      {listing.status === "sold" && <Badge className="bg-red-100 text-red-700 text-[10px]">Sold</Badge>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {subTab === "business" && (
        businessListings.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
            <p className="text-sm text-[#6B665E] font-['Nunito']">No business listings</p>
          </div>
        ) : (
          <div className="space-y-3">
            {businessListings.map(listing => (
              <Card key={listing.listing_id} className="bg-white border-[#E5E0D5] shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/directory/${listing.listing_id}`)} data-testid={`business-listing-${listing.listing_id}`}>
                <CardContent className="p-4 flex gap-4">
                  {listing.photos?.[0] && <img src={resolveImageUrl(listing.photos[0])} alt={listing.name} className="w-16 h-16 rounded-md object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26] truncate">{listing.name}</h3>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1 line-clamp-1">{listing.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[10px]">{listing.category}</Badge>
                      {listing.rating > 0 && <span className="flex items-center gap-0.5 text-[10px] text-[#D4AF37]"><Star className="w-3 h-3 fill-current" /> {listing.rating?.toFixed(1)}</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}

